<template>
  <BaseLayout title="Queue & Workers Overview">
    <div class="container my-4">
      <h1 class="display-4 text-center">Queue & Workers Overview</h1>

      <div v-if="loadingPage" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>

      <div v-else>
        <!-- System Health Section -->
        <div :class="['alert d-flex align-items-center mb-3', 'alert-' + statusColor(health.status)]">
          <i :class="['bi', statusIcon(health.status), 'me-2', 'fs-5']"></i>
          <strong>System status: {{ health.status }}</strong>
        </div>
        <div class="row mb-4" v-if="health.components">
          <div class="col-md-4 mb-2" v-for="(status, name) in health.components" :key="name">
            <div :class="['card', 'border-' + statusColor(status)]">
              <div class="card-body d-flex align-items-center">
                <i :class="['bi', statusIcon(status), 'me-2', 'fs-4', 'text-' + statusColor(status)]"></i>
                <div>
                  <strong class="text-capitalize">{{ name }}</strong>
                  <div :class="['small', 'text-' + statusColor(status)]">{{ status }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary Section -->
        <div class="card mb-4">
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <h5 class="card-title">Tasks Summary</h5>
                <ul class="list-group">
                  <li class="list-group-item d-flex align-items-center">
                  <i class="bi bi-cpu text-info me-2"></i>
                  <span><strong>Workers - Working on tasks: {{ getNumberOfActiveWorkers() }}</strong></span>
                  </li>
                  <li class="list-group-item d-flex align-items-center">
                  <i class="bi bi-card-list text-primary me-2"></i>
                    <span><strong>Queue - Total tasks: {{ getQueueTotalTasks() }}</strong></span>
                  </li>
                  <li class="list-group-item">
                  <div class="ms-4">
                    <ul class="list-unstyled">
                    <li class="d-flex align-items-center">
                      <i class="bi bi-patch-check text-success me-2"></i>
                      <span>Evaluations: {{ getTaskCount('evaluation') }}</span>
                    </li>
                    <li class="d-flex align-items-center">
                      <i class="bi bi-shield-exclamation text-warning me-2"></i>
                      <span>Sanity Checks: {{ getTaskCount('pre_check') }}</span>
                    </li>
                    <li class="d-flex align-items-center">
                      <i class="bi bi-database text-info me-2"></i>
                      <span>Generate Dataset: {{ getTaskCount('generate_dataset') }}</span>
                    </li>
                    </ul>
                  </div>
                  </li>
                </ul>
              </div>
              <div v-if="queueData && queueData.total_tasks > 0" class="col-md-6">
                <h5>
                  Queue - Evaluation Summaries
                  <i class="bi bi-question-circle" v-tooltip="'Summaries of evaluations in waiting queue, grouped by homework and deadline.'"></i>
                </h5>
                <ul class="list-group" style="overflow-y: auto; max-height: 20vh;">
                  <li v-for="summary in evaluationSummaries" :key="summary.deadline.id" class="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <div>
                        <i class="bi bi-journal-text text-primary me-2"></i>{{ getHomeworkTitle(summary.homework_title) }}
                      </div>
                      <small>
                        Deadline {{ formatDate(summary.deadline.date) }} -
                        <i class="bi bi-tag text-info me-1"></i>{{ getEvaluationTypeLabel(summary.deadline.evaluation_type) }}
                      </small>
                    </div>
                    <span class="badge bg-primary rounded-pill">{{ summary.count }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="my-4">
          <p class="text-center">
            <i class="bi bi-info-circle me-2"></i>
            Below are detailed insights of tasks in the queue and in active workers.
          </p>
        </div>
        <!-- Accordion Section -->
        <div class="accordion" id="mainAccordion">
          <!-- Task List Section -->
          <div class="accordion-item">
            <h2 class="accordion-header" id="tasksHeading">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#tasksCollapse"
                aria-expanded="false"
                aria-controls="tasksCollapse"
              >
                <span>
                  <i class="bi bi-card-list text-primary me-2"></i>
                </span>
                Tasks in Queue
              </button>
            </h2>
            <div
              id="tasksCollapse"
              class="accordion-collapse collapse"
              aria-labelledby="tasksHeading"
              data-bs-parent="#mainAccordion"
            >
              <div class="accordion-body">
                <div v-if="loadingTasks" class="text-center my-4">
                  <div class="spinner-border" role="status">
                    <span class="sr-only">&nbsp;&nbsp;Loading...</span>
                  </div>
                </div>
                <div v-else-if="!queueData.tasks" class="alert alert-danger text-center">
                  <i class="bi bi-exclamation-triangle text-danger me-2"></i>Unable to fetch task data.
                </div>
                <div v-else-if="queueData.tasks.length === 0" class="alert alert-info text-center">
                  <i class="bi bi-info-circle text-info me-2"></i>No tasks in the queue.
                </div>
                <div v-else>
                  <table class="table table-striped">
                    <thead>
                      <tr>
                        <th><i class="bi bi-clock text-primary me-2"></i>Submission Time</th>
                        <th><i class="bi bi-person text-primary me-2"></i>Student UCO</th>
                        <th><i class="bi bi-gear text-primary me-2"></i>Task Type</th>
                        <th style="max-width: 20vw;"><i class="bi bi-book text-primary me-2"></i>Homework</th>
                        <th><i class="bi bi-calendar text-primary me-2"></i>Deadline</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="task in paginatedTasks"
                        :key="task.submission_time + task.uco"
                      >
                        <td>{{ formDateQueue(task.submission_time) }}</td>
                        <td>{{ task.uco }}</td>
                        <td>{{ getQueueTypeLabel(task.task_type) }}</td>
                        <td>{{ getHomeworkTitle(task.homework_title) }}</td>
                        <td>
                          <div v-if="task.deadline">
                            {{ formatDate(task.deadline.date) }} ({{ getEvaluationTypeLabel(task.deadline.evaluation_type) }})
                          </div>
                          <div v-else>
                            -
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <!-- Pagination Controls -->
                  <nav>
                    <ul class="pagination justify-content-center">
                      <li
                        class="page-item"
                        :class="{ disabled: currentPage === 1 }"
                      >
                        <button
                          class="page-link"
                          @click="changePage(currentPage - 1)"
                        >
                          Previous
                        </button>
                      </li>
                      <li
                        class="page-item"
                        v-for="page in totalPages"
                        :key="page"
                        :class="{ active: currentPage === page }"
                      >
                        <button
                          class="page-link"
                          @click="changePage(page)"
                        >
                          {{ page }}
                        </button>
                      </li>
                      <li
                        class="page-item"
                        :class="{ disabled: currentPage === totalPages }"
                      >
                        <button
                          class="page-link"
                          @click="changePage(currentPage + 1)"
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>

          <!-- Worker Summary Section -->
          <div class="accordion-item">
            <h2 class="accordion-header" id="workersHeading">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#workersCollapse"
                aria-expanded="false"
                aria-controls="workersCollapse"
              >
                <span>
                  <i class="bi bi-cpu text-info me-2"></i>
                  Worker Summary (Max Workers: {{ getMaxWorkers() }})
                </span>
              </button>
            </h2>
            <div
              id="workersCollapse"
              class="accordion-collapse collapse"
              aria-labelledby="workersHeading"
              data-bs-parent="#mainAccordion"
            >
              <div class="accordion-body">
                <div v-if="loadingWorkers" class="text-center my-4">
                  <div class="spinner-border" role="status">
                    <span class="sr-only">&nbsp;&nbsp;Loading...</span>
                  </div>
                </div>
                <div v-else-if="!workerData.workers_summaries || !workerData.max_workers" class="alert alert-danger text-center">
                  <i class="bi bi-exclamation-triangle text-danger me-2"></i>Unable to fetch worker data.
                </div>
                <div v-else-if="!workerData.workers_summaries || workerData.workers_summaries.length === 0" class="alert alert-info text-center">
                  <i class="bi bi-info-circle text-info me-2"></i>No active workers.
                </div>
                <div v-else>
                  <table class="table table-striped">
                    <thead>
                      <tr>
                        <th><i class="bi bi-clock text-primary me-2"></i>Time Started</th>
                        <th><i class="bi bi-person text-primary me-2"></i>Student UCO</th>
                        <th><i class="bi bi-gear text-primary me-2"></i>Worker Type</th>
                        <th><i class="bi bi-book text-primary me-2"></i>Homework</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="worker in workerData.workers_summaries"
                        :key="worker.time_started + worker.uco"
                      >
                        <td>{{ formDateQueue(worker.time_started) }}</td>
                        <td>{{ worker.uco }}</td>
                        <td>{{ getWorkerTypeLabel(worker.worker_type) }}</td>
                        <td>{{ getHomeworkTitle(worker.homework_title) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div class="card p-3">
        <h5 class="card-title">Quick Actions:</h5>
        <button @click="fecthAllData" class="btn btn-primary" v-tooltip="'This will get the latest data from the server.'">
          <i class="bi bi-arrow-clockwise me-2"></i>
          Refresh Data
        </button>
        <button @click="cancelAllQueueTasks" class="btn btn-danger mt-2" v-tooltip="'This will cancel and remove all tasks currently in the queue. Tasks in workers will finish.'">
          <i class="bi bi-x-circle me-2"></i>
          <span v-if="loadingCancel" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Cancel All Tasks
        </button>
      </div>
    </template>

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import StemErrorModal from '@/components/StemErrorModal.vue';
import QueueController from '@/controllers/QueueController';
import HealthController from '@/controllers/HealthController';
import QueueResponse, { WorkerResponse } from '@/models/queue';
import Deadline from '@/models/deadline';
import Queue from '@/models/queue';
import { mapGetters } from 'vuex';

const HEALTH_POLL_INTERVAL_MS = 30000;

export default {
  name: 'QueueView',
  components: {
    BaseLayout,
    StemErrorModal,
  },
  data() {
    return {
      queueData: new QueueResponse({}),
      workerData: new WorkerResponse({}),
      loadingPage: true,
      loadingTasks: false,
      loadingWorkers: false,
      loadingCancel: false,
      currentPage: 1,
      tasksPerPage: 10,
      health: { status: 'unknown', components: {} },
      healthInterval: null,
    };
  },
  computed: {
    ...mapGetters(['isTeacher']),
    totalPages() {
      return Math.ceil(this.queueData.tasks.length / this.tasksPerPage);
    },
    paginatedTasks() {
      const start = (this.currentPage - 1) * this.tasksPerPage;
      const end = start + this.tasksPerPage;
      return this.queueData.tasks.slice(start, end);
    },
    evaluationSummaries() {
      return this.queueData.per_deadline_summary.filter(
        (summary) => summary.task_type === 'evaluation'
      );
    },
  },
  async created() {
    // Check if the user is a teacher
    if (!this.isTeacher) {
      this.$store.dispatch('triggerAlert', {
        type: 'error',
        title: 'Error',
        message: 'You are not authorized to access this page.',
      });
      this.$router.push('/');
      return;
    }
    await this.fetchQueueData();
    await this.fetchWorkerData();
    await this.fetchHealth();
    this.startHealthPolling();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    this.stopHealthPolling();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  methods: {
    async fetchHealth() {
      try {
        this.health = await HealthController.fetchHealth();
      } catch (error) {
        this.health = { status: 'error', components: null };
      }
    },
    // Browsers throttle background timers but don't stop them — pausing while the
    // tab is hidden avoids polling /health (DB query + broker RPC) for no reason.
    startHealthPolling() {
      if (this.healthInterval) return;
      this.healthInterval = setInterval(this.fetchHealth, HEALTH_POLL_INTERVAL_MS);
    },
    stopHealthPolling() {
      clearInterval(this.healthInterval);
      this.healthInterval = null;
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.fetchHealth();
        this.startHealthPolling();
      } else {
        this.stopHealthPolling();
      }
    },
    statusColor(status) {
      return { ok: 'success', degraded: 'warning', no_workers: 'warning', error: 'danger' }[status] || 'secondary';
    },
    statusIcon(status) {
      return {
        ok: 'bi-check-circle-fill',
        degraded: 'bi-exclamation-triangle-fill',
        no_workers: 'bi-exclamation-triangle-fill',
        error: 'bi-x-circle-fill',
      }[status] || 'bi-exclamation-triangle-fill';
    },
    async fecthAllData() {
      await this.fetchQueueData();
      await this.fetchWorkerData();
      await this.fetchHealth();
    },
    async fetchQueueData() {
      try {
        this.loadingPage = true;
        this.loadingTasks = true;
        this.queueData = await QueueController.fetchQueueTasksWithSummary();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loadingPage = false;
        this.loadingTasks = false;
      }
    },
    async fetchWorkerData() {
      try {
        this.loadingPage = true;
        this.loadingWorkers = true;
        this.workerData = await QueueController.fetchWorkerSummary();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loadingPage = false;
        this.loadingWorkers = false;
      }
    },
    async cancelAllQueueTasks() {
      try {
        if (!confirm('Do you want to cancel and remove all tasks in the queue? \nNOTE: All tasks will be removed from queue. Evaluation tasks will have canceled info.\nNOTE: Tasks in workers will finish.')) {
          return;
        }
        this.loadingCancel = true;
        await QueueController.cancelAllQueueTasks();
        this.$store.dispatch('triggerAlert', {
          title: 'Success',
          message: 'All tasks in the queue have been cancelled. Queue ',
          type: 'success',
        });
        await this.fetchQueueData();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loadingCancel = false;
      }
    },
    getTaskCount(taskType) {
      if (!this.queueData || !this.queueData.per_deadline_summary) {
        return 'N/A';
      }

      const summary = this.queueData.per_deadline_summary.filter(
        (item) => item.task_type === taskType
      );
      return summary.reduce((acc, item) => acc + item.count, 0);
    },
    getMaxWorkers() {
      return this.workerData.max_workers || 'N/A';
    },
    getQueueTotalTasks() {
      if (this.queueData.total_tasks == null)
        return 'N/A';

      return this.queueData.total_tasks;
    },
    getNumberOfActiveWorkers() {
      return this.workerData.workers_summaries ? this.workerData.workers_summaries.length : 'N/A';
    },
    formatDate(date) {
      return date ? Deadline.formatDateTime(date) : 'N/A';
    },
    getHomeworkTitle(homework) {
      return homework ? homework : '-';
    },
    formDateQueue(date) {
      return date ? Queue.formatDateTime(date) : 'N/A';
    },
    getQueueTypeLabel(type) {
      return type ? QueueResponse.getQueueTypeLabel(type) : 'N/A';
    },
    getWorkerTypeLabel(type) {
      return type ? WorkerResponse.getWorkerTypeLabel(type) : 'N/A';
    },
    getEvaluationTypeLabel(type) {
      return type ? Deadline.getEvaluationTypeLabel(type) : 'N/A';
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  }
};
</script>